import { Injectable, signal } from '@angular/core';

export type NotifyVariant = 'success' | 'error' | 'warning' | 'info';

export interface NotifyOptions {
  /** ประเภทการแจ้งเตือน */
  variant: NotifyVariant;
  /** หัวข้อ */
  title: string;
  /** รายละเอียด (ถ้ามี) */
  detail?: string;
  /** ชื่อ icon (Material Symbols Outlined) — default ตาม variant */
  icon?: string;
  /** ข้อความปุ่ม OK (default 'ปิด') */
  okText?: string;
  /** auto-dismiss หลังกี่ ms (0 = ไม่ auto-dismiss) */
  autoDismissMs?: number;
  /** callback เมื่อกด OK หรือ auto-dismiss */
  onClose?: () => void;
}

export interface ConfirmOptions {
  /** ประเภท (สีของปุ่ม/icon) */
  variant?: 'danger' | 'warning' | 'info';
  /** หัวข้อ */
  title: string;
  /** ข้อความหลัก */
  message: string;
  /** ข้อความย่อย (เล็กกว่า) */
  subMessage?: string;
  /** ชื่อ icon (default 'help') */
  icon?: string;
  /** ข้อความปุ่มยืนยัน (default 'ยืนยัน') */
  confirmText?: string;
  /** ข้อความปุ่มยกเลิก (default 'ยกเลิก') */
  cancelText?: string;
}

interface NotifyState {
  visible: boolean;
  options: NotifyOptions | null;
}

interface ConfirmState {
  visible: boolean;
  options: ConfirmOptions | null;
  loading: boolean;
  resolve: ((value: boolean) => void) | null;
}

const DEFAULT_ICONS: Record<NotifyVariant, string> = {
  success: 'check_circle',
  error: 'error',
  warning: 'warning',
  info: 'info',
};

@Injectable({ providedIn: 'root' })
export class NotifyService {
  /** สถานะ notify dialog */
  readonly notify$ = signal<NotifyState>({ visible: false, options: null });

  /** สถานะ confirm dialog */
  readonly confirm$ = signal<ConfirmState>({ visible: false, options: null, loading: false, resolve: null });

  // ─────────── Notify (แจ้งเตือน) ───────────

  /** แสดง notify popup กลางจอ */
  notify(options: NotifyOptions): void {
    const opts = { icon: DEFAULT_ICONS[options.variant], okText: 'ปิด', autoDismissMs: 0, ...options };
    this.notify$.set({ visible: true, options: opts });

    if (opts.autoDismissMs && opts.autoDismissMs > 0) {
      setTimeout(() => this.closeNotify(), opts.autoDismissMs);
    }
  }

  /** แจ้งสำเร็จ */
  success(title: string, detail?: string, onClose?: () => void): void {
    this.notify({ variant: 'success', title, detail, onClose });
  }

  /** แจ้งสำเร็จแล้ว auto-dismiss + callback */
  successAndGo(title: string, detail: string, onClose: () => void, delayMs = 1500): void {
    this.notify({ variant: 'success', title, detail, autoDismissMs: delayMs, onClose });
  }

  /** แจ้ง error */
  error(title: string, detail?: string): void {
    this.notify({ variant: 'error', title, detail });
  }

  /** แจ้ง warning */
  warning(title: string, detail?: string): void {
    this.notify({ variant: 'warning', title, detail });
  }

  /** แจ้ง info */
  info(title: string, detail?: string): void {
    this.notify({ variant: 'info', title, detail });
  }

  /** ปิด notify popup */
  closeNotify(): void {
    const state = this.notify$();
    const onClose = state.options?.onClose;
    this.notify$.set({ visible: false, options: null });
    onClose?.();
  }

  // ─────────── Confirm (ยืนยัน) ───────────

  /** แสดง confirm popup กลางจอ — return Promise<boolean> */
  confirm(options: ConfirmOptions): Promise<boolean> {
    return new Promise(resolve => {
      this.confirm$.set({
        visible: true,
        options: { variant: 'warning', icon: 'help', confirmText: 'ยืนยัน', cancelText: 'ยกเลิก', ...options },
        loading: false,
        resolve,
      });
    });
  }

  /** ตั้ง loading สถานะของ confirm dialog */
  setConfirmLoading(loading: boolean): void {
    this.confirm$.update(s => ({ ...s, loading }));
  }

  /** resolve confirm แล้วปิด */
  resolveConfirm(value: boolean): void {
    const state = this.confirm$();
    state.resolve?.(value);
    this.confirm$.set({ visible: false, options: null, loading: false, resolve: null });
  }
}
